import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material'
import FormControl from '@mui/material/FormControl'
import clamp from 'lodash-es/clamp'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type ModelSampling,
  type ModelSamplingDiscreteSamplingType,
  type ModelSamplingInputs,
  modelSamplingDiscreteSamplingTypes,
} from 'src/components/GenerationModel/GenerationModelEdit/ModelSubmitForms/ModelSubmitFormProvider'
import { ValueSliderWithInput } from 'src/components/ui/Slider'
import { UIInput } from 'src/components/ui/TextField'
import type { HandleChange } from 'src/utils/useForm'
import type { TaskSubmitFormValues } from '../../BaseTaskSubmitForm'

function ValueInputWithRange({
  min,
  max,
  value,
  onChange,
  disabled,
}: {
  min: number
  max: number
  value?: number
  onChange?: (value: number) => void
  disabled?: boolean
}) {
  const [inputValue, setInputValue] = useState(String(value))

  useEffect(() => {
    setInputValue(String(value))
  }, [value])

  function onInputBlur() {
    const clampedVal = clamp(Number(inputValue), min, max)
    onChange?.(clampedVal)
  }

  return (
    <UIInput
      variant="rounded-normal"
      inputMode="numeric"
      onChange={ev => setInputValue(ev.target.value)}
      onBlur={onInputBlur}
      value={inputValue}
      className={'w-full rounded-[10px]'}
      disabled={disabled}
    />
  )
}

// Default values comes from https://github.com/comfyanonymous/ComfyUI/blob/f1059b0b82619cb43f1359f4bb4aee4495c17309/comfy_extras/nodes_model_advanced.py
const modelSamplingTypes = [
  'ModelSamplingDiscrete',
  'ModelSamplingSD3',
  'ModelSamplingFlux',
] as const satisfies (keyof ModelSamplingInputs)[]
export const defaultModelSampings: Record<
  keyof ModelSamplingInputs,
  ModelSampling
> = {
  ModelSamplingDiscrete: {
    type: 'ModelSamplingDiscrete',
    inputs: {
      sampling: 'eps',
      zsnr: false,
    },
  } satisfies ModelSampling<'ModelSamplingDiscrete'>,
  ModelSamplingSD3: {
    type: 'ModelSamplingSD3',
    inputs: {
      shift: 3,
    },
  } satisfies ModelSampling<'ModelSamplingSD3'>,
  ModelSamplingFlux: {
    type: 'ModelSamplingFlux',
    inputs: {
      max_shift: 1.15,
      base_shift: 0.5,
      width: 1024,
      height: 1024,
    },
  } satisfies ModelSampling<'ModelSamplingFlux'>,
}

function ModelSamplingTypeSelect({
  value,
  onChange,
  disabled,
}: {
  value?: ModelSampling
  onChange?: (value: ModelSampling) => void
  disabled?: boolean
}) {
  const onTypeChange = useCallback(
    (event: SelectChangeEvent<keyof ModelSamplingInputs>) => {
      onChange?.(
        defaultModelSampings[event.target.value as keyof ModelSamplingInputs],
      )
    },
    [onChange],
  )
  return (
    <FormControl fullWidth>
      <Select
        disabled={disabled}
        size="small"
        value={value?.type}
        onChange={onTypeChange}
      >
        {modelSamplingTypes.map(key => {
          return (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

export function ModelSamplingDiscreteControl({
  value,
  onChange,
  disabled,
}: {
  value?: ModelSampling<'ModelSamplingDiscrete'>
  onChange?: (value: ModelSampling<'ModelSamplingDiscrete'>) => void
  disabled?: boolean
}) {
  const { t } = useTranslation(['generation'])

  return (
    <>
      <div className="flex gap-2 items-center">
        <h2 className="text-[14px]">
          {t('generation:model-sampling-discrete.sampling.label')}
        </h2>
      </div>
      <FormControl fullWidth>
        <Select
          disabled={disabled}
          size="small"
          value={value?.inputs.sampling}
          onChange={event => {
            if (value) {
              onChange?.({
                ...value,
                inputs: {
                  ...value.inputs,
                  sampling: event.target
                    .value as ModelSamplingDiscreteSamplingType,
                },
              })
            }
          }}
        >
          {modelSamplingDiscreteSamplingTypes.map(key => {
            return (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            )
          })}
        </Select>
      </FormControl>
      <FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={value?.inputs.zsnr}
              onChange={(_, checked) => {
                if (value) {
                  onChange?.({
                    ...value,
                    inputs: {
                      ...value.inputs,
                      zsnr: checked,
                    },
                  })
                }
              }}
            />
          }
          disabled={disabled}
          label={t('generation:model-sampling-discrete.zsnr.label')}
          labelPlacement="start"
        />
      </FormControl>
    </>
  )
}

const MIN_SHIFT = 0
const MAX_SHIFT = 100
const SHIFT_STEP = 0.1

export function ModelSamplingSD3Control({
  value,
  onChange,
  disabled,
}: {
  value?: ModelSampling<'ModelSamplingSD3'>
  onChange?: (value: ModelSampling<'ModelSamplingSD3'>) => void
  disabled?: boolean
}) {
  const { t } = useTranslation(['generation'])

  return (
    <>
      <div className="flex gap-2 items-center">
        <h2 className="text-[14px]">
          {t('generation:model-sampling-sd3.shift.label')}
        </h2>
      </div>
      <ValueSliderWithInput
        className="w-full"
        min={MIN_SHIFT}
        max={MAX_SHIFT}
        step={SHIFT_STEP}
        value={value?.inputs.shift}
        onChange={shift => {
          if (value) {
            onChange?.({
              ...value,
              inputs: {
                ...value?.inputs,
                shift,
              },
            })
          }
        }}
        disabled={disabled}
      />
    </>
  )
}

const MIN_RESOLUTION = 16
const MAX_RESOLUTION = 16384

export function ModelSamplingFluxControl({
  value,
  onChange,
  disabled,
}: {
  value?: ModelSampling<'ModelSamplingFlux'>
  onChange?: (value: ModelSampling<'ModelSamplingFlux'>) => void
  disabled?: boolean
}) {
  const { t } = useTranslation(['generation'])

  return (
    <>
      <div className="flex gap-2 items-center">
        <h2 className="text-[14px]">
          {t('generation:model-sampling-flux.max-shift.label')}
        </h2>
      </div>
      <ValueSliderWithInput
        className="w-full"
        min={MIN_SHIFT}
        max={MAX_SHIFT}
        step={SHIFT_STEP}
        value={value?.inputs.max_shift}
        onChange={max_shift => {
          if (value) {
            onChange?.({
              ...value,
              inputs: {
                ...value?.inputs,
                max_shift,
              },
            })
          }
        }}
        disabled={disabled}
      />
      <div className="flex gap-2 items-center">
        <h2 className="text-[14px]">
          {t('generation:model-sampling-flux.base-shift.label')}
        </h2>
      </div>
      <ValueSliderWithInput
        className="w-full"
        min={MIN_SHIFT}
        max={MAX_SHIFT}
        step={SHIFT_STEP}
        value={value?.inputs.base_shift}
        onChange={base_shift => {
          if (value) {
            onChange?.({
              ...value,
              inputs: {
                ...value?.inputs,
                base_shift,
              },
            })
          }
        }}
        disabled={disabled}
      />
      <div className="flex gap-2 items-center">
        <h2 className="text-[14px]">
          {t('generation:model-sampling-flux.width.label')}
        </h2>
      </div>
      <ValueInputWithRange
        min={MIN_RESOLUTION}
        max={MAX_RESOLUTION}
        value={value?.inputs.width}
        onChange={width => {
          if (value) {
            onChange?.({
              ...value,
              inputs: {
                ...value?.inputs,
                width,
              },
            })
          }
        }}
        disabled={disabled}
      />
      <div className="flex gap-2 items-center">
        <h2 className="text-[14px]">
          {t('generation:model-sampling-flux.height.label')}
        </h2>
      </div>
      <ValueInputWithRange
        min={MIN_RESOLUTION}
        max={MAX_RESOLUTION}
        value={value?.inputs.height}
        onChange={height => {
          if (value) {
            onChange?.({
              ...value,
              inputs: {
                ...value?.inputs,
                height,
              },
            })
          }
        }}
        disabled={disabled}
      />
    </>
  )
}

export function ModelSamplingControl({
  value,
  onChange,
  disabled,
}: {
  value?: ModelSampling
  onChange?: (value: ModelSampling) => void
  disabled?: boolean
}) {
  const { t } = useTranslation(['generation'])

  return (
    <div className="flex flex-col gap-2 items-start">
      <div className="flex gap-2 items-center">
        <h2 className="text-[14px]">
          {t('generation:model-sampling-type.label')}
        </h2>
      </div>
      <ModelSamplingTypeSelect
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      {value?.type === 'ModelSamplingDiscrete' ? (
        <ModelSamplingDiscreteControl
          value={value as ModelSampling<'ModelSamplingDiscrete'>}
          onChange={onChange}
          disabled={disabled}
        />
      ) : value?.type === 'ModelSamplingSD3' ? (
        <ModelSamplingSD3Control
          value={value as ModelSampling<'ModelSamplingSD3'>}
          onChange={onChange}
          disabled={disabled}
        />
      ) : value?.type === 'ModelSamplingFlux' ? (
        <ModelSamplingFluxControl
          value={value as ModelSampling<'ModelSamplingFlux'>}
          onChange={onChange}
          disabled={disabled}
        />
      ) : null}
    </div>
  )
}

const MIN_RESCALE_CFG = 0
const MAX_RESCALE_CFG = 1
const RESCALE_CFG_STEP = 0.01

export function RescaleCFGSelect({
  value,
  onChange,
  disabled,
}: {
  value?: number
  onChange?: (value: number) => void
  sliderClassName?: string
  disabled?: boolean
}) {
  const { t } = useTranslation(['generation'])

  return (
    <div>
      <div className="flex gap-2 items-center mb-2">
        <h2 className="text-[14px]">{t('generation:rescale-cfg.label')}</h2>
      </div>

      <RescaleCFGInner
        className="flex gap-8 w-full"
        minRescaleCfgValue={MIN_RESCALE_CFG}
        maxRescaleCfgValue={MAX_RESCALE_CFG}
        step={RESCALE_CFG_STEP}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  )
}

export function RescaleCFGInner({
  value,
  handleChange,
  onChange,
  sliderClassName,
  disabled,
  minRescaleCfgValue = MIN_RESCALE_CFG,
  maxRescaleCfgValue = MAX_RESCALE_CFG,
  step = RESCALE_CFG_STEP,
  className,
}: {
  value?: number
  handleChange?: HandleChange<keyof TaskSubmitFormValues>
  onChange?: (value: number) => void
  sliderClassName?: string
  disabled?: boolean
  minRescaleCfgValue?: number
  maxRescaleCfgValue?: number
  step?: number
  className?: string
}) {
  return (
    <ValueSliderWithInput
      className={className}
      min={minRescaleCfgValue}
      step={step}
      max={maxRescaleCfgValue}
      value={value}
      onChange={val =>
        handleChange
          ? handleChange('inferenceConfig.rescaleCFG')(val)
          : onChange?.(val)
      }
      disabled={disabled}
      sliderClassName={sliderClassName}
    />
  )
}
